/**
 * Aufträge — die Gesamtübersicht aller Touren und die Detailsicht dazu.
 *
 * **Der Zustand wird nicht gesetzt, er ergibt sich.** Das ist die wichtigste
 * Änderung gegenüber der ersten Fassung. Vorher musste das Büro jeden Auftrag
 * von Hand durch „disponiert" und „läuft" schieben — Arbeit, die niemand tun
 * will und die deshalb niemand zuverlässig tut. Ein Statusfeld, das keiner
 * pflegt, ist schlimmer als keines: Man glaubt ihm.
 *
 * Jetzt rechnet die Datenbanksicht `auftrag_uebersicht` den Zustand aus dem
 * aus, was ohnehin passiert: Ladedatum, abgeschlossene Fahrten, Beleglage,
 * Rechnungsnummer. Das Büro trägt eine Tour ein und sieht danach zu.
 *
 * `transportauftrag.status` gibt es weiterhin, wird vom Büro aber nicht mehr
 * bedient. Die Fahrer-App und die Übergangsregeln stützen sich noch darauf.
 */

import { supabase } from '../supabase';
import { aendern, anlegen, NEUER_STAND_GELADEN, Speicherfehler } from './speichern';
import { alsKlarfehler } from './fehler';

/** Die Arten von `Marker` (gestaltung/bausteine.tsx). */
export type Markerart = 'gut' | 'gut_voll' | 'warn' | 'schlecht' | 'neutral';

export type Zustand =
  | 'geplant'
  | 'tour_offen'
  | 'belege_fehlen'
  /** Alle Fakten liegen vor — es fehlt nur noch die Entscheidung des Büros. */
  | 'abrechnungsreif'
  /** Vom Büro freigegeben. Erst dieser Zustand erscheint in „Aufträge abrechnen". */
  | 'abrechenbar'
  | 'abgerechnet'
  | 'storniert'
  | 'ohne_fahrt';

/**
 * Die Beschriftungen sind die, die Lukas benutzt — nicht die der Datenbank.
 * „Belege verfügbar" heißt: Der Fahrer hat die Papiere in der App, du kannst
 * abrechnen.
 */
export const ZUSTANDTEXT: Record<Zustand, string> = {
  geplant: 'Geplant',
  tour_offen: 'Tour offen',
  belege_fehlen: 'Belege fehlen',
  abrechnungsreif: 'Kann abgerechnet werden',
  abrechenbar: 'Zur Abrechnung freigegeben',
  abgerechnet: 'Abgerechnet',
  storniert: 'Storniert',
  ohne_fahrt: 'Ohne Fahrt',
};

export const ZUSTANDFARBE: Record<Zustand, Markerart> =
  {
    geplant: 'neutral',
    tour_offen: 'warn',
    belege_fehlen: 'warn',
    // ═══ ZWEI GRÜN — ABER NICHT ZWEI GRÜNTÖNE (HJ-031) ═══
    //
    // Vorher war „Kann abgerechnet werden" grau, mit dem Argument, zwei
    // Grüntöne lese niemand. Im Alltag ging aber genau dieser Zustand unter —
    // er ist der, bei dem das Büro etwas tun kann. Jetzt grün wie alles
    // Erledigte; „Zur Abrechnung freigegeben" unterscheidet sich nicht durch
    // einen Farbton, sondern durch die FORM: gefüllt und mit Haken (`gut_voll`
    // in `Marker`). Das liest man auch bei Farbschwäche.
    abrechnungsreif: 'gut',
    abrechenbar: 'gut_voll',
    abgerechnet: 'neutral',
    storniert: 'schlecht',
    ohne_fahrt: 'schlecht',
  };

/**
 * Was die Beleglage aus der Datenbank bedeutet — in einem Satz, den man im
 * Büro versteht. Die Datenbank antwortet mit Kürzeln wie
 * `eingangslieferschein+ausgangslieferschein_fehlt`.
 */
export function beleglageText(roh: string | null): {
  text: string;
  art: 'gut' | 'warn' | 'neutral';
} {
  if (!roh || roh === 'unbekannt') return { text: '—', art: 'neutral' };
  if (roh === 'vollstaendig') return { text: 'vollständig', art: 'gut' };

  const fehlt = roh
    .replace(/_fehlt$/, '')
    .split('+')
    .map((teil) => {
      if (teil === 'eingangslieferschein') return 'Eingang';
      if (teil === 'ausgangslieferschein') return 'Ausgang';
      if (teil === 'nachweis') return 'Nachweis';
      if (teil === 'papiere_vom_subunternehmer') return 'Papiere vom Subunternehmer';
      return teil;
    });

  return { text: `${fehlt.join(' und ')} fehlt`, art: 'warn' };
}

/**
 * Auf ganze Cent runden — und zwar so, wie ein Mensch es tut.
 *
 * ═══ WARUM NICHT EINFACH `toFixed(2)` ═══
 *
 * 27,3 t × 12,35 € = 337,155. Binär ist das 337,15499999… — `toFixed(2)` rundet
 * es auf 337,15 ab, ein Mensch auf 337,16. Bildschirm und CSV rundeten vorher an
 * verschiedenen Stellen (der Bildschirm gar nicht, die CSV je Zeile), und die
 * Summe neben dem Knopf wich um Cent von der Summe der Datei ab. Jetzt rundet
 * `betrag()` einmal, an der Quelle: Was hier herauskommt, ist ein Centbetrag,
 * und alle Summen addieren Centbeträge.
 *
 * `toPrecision(12)` räumt das Rauschen der Binärdarstellung weg, bevor
 * gerundet wird; 12 Stellen reichen für jeden Betrag, den es hier geben kann.
 */
export function aufCent(wert: number): number {
  return Math.round(Number((wert * 100).toPrecision(12))) / 100;
}

/** Eine Summe aus Centbeträgen — selbst wieder auf Cent, gegen Additionsrauschen. */
export function summeAufCent(werte: number[]): number {
  return aufCent(werte.reduce((n, w) => n + w, 0));
}

/** Der Betrag, soweit er sich überhaupt ausrechnen lässt. */
export function betrag(zeile: Auftragszeile): number | null {
  if (zeile.preis_vk === null || zeile.preisart === null) return null;
  if (zeile.preisart === 'pauschale') return aufCent(Number(zeile.preis_vk));
  if (zeile.preisart === 'menge') {
    // Beim Stundensatz gibt es bewusst KEINE Zahl: Die Stunden stehen am
    // Arbeitstag des Fahrers, nicht am Auftrag. Eine geratene Zahl auf einer
    // Rechnung wäre schlimmer als gar keine.
    const menge = zeile.mengeneinheit === 'fuhre' ? zeile.fuhren : zeile.menge;
    // Auch die Null ist hier „noch keine Menge" und nicht „null Euro": Die
    // Fuhrenzahl kommt aus einer Zählung und steht bei einer noch nicht
    // gefahrenen Tour auf 0. Ein Betrag von 0,00 € in der Spalte sähe aus wie
    // ein gerechneter Preis — und würde in der Vorausschau als vollständige
    // Zeile mitzählen, obwohl noch nichts feststeht.
    if (menge === null || Number(menge) === 0) return null;
    return aufCent(Number(zeile.preis_vk) * Number(menge));
  }
  return null;
}

/**
 * Fehlt dieser Tour ein Betrag, der auf die Rechnung gehört?
 *
 * Der Stundensatz zählt nicht: Dort gibt es bewusst keine Zahl am Auftrag, das
 * ist die Bauart und kein Mangel. Alles andere ohne Betrag heißt: Preis, Preisart
 * oder Menge fehlt — und in eine Rechnung gehört keine Zeile mit leerer
 * Betragszelle.
 */
export function ohneBetrag(zeile: Auftragszeile): boolean {
  return zeile.preisart !== 'stundensatz' && betrag(zeile) === null;
}

export function euro(wert: number | null): string {
  if (wert === null) return '—';
  return wert.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

/** „Menge in t" — die Beschriftung nach Einheit. */
export function mengenmarke(einheit: string | null): string {
  switch (einheit) {
    case 'm3':
      return 'Menge in m³';
    case 'stueck':
      return 'Menge in Stück';
    default:
      return 'Menge in t';
  }
}

export const PREISARTTEXT: Record<string, string> = {
  pauschale: 'Pauschale',
  menge: 'Nach Menge',
  stundensatz: 'Stundensatz',
};

export type Auftragszeile = {
  id: string;
  nummer: string | null;
  externe_nummer: string | null;
  /**
   * Die Kennung gegenüber dem Kunden: seine eigene Nummer — und bei
   * Pendelfahrten, wo es keine gibt, eine aus Kunde, Datum und Kennzeichen
   * gebildete. Berechnet, nicht gespeichert: Wird der Wagen abends korrigiert,
   * folgt sie mit.
   */
  referenz: string | null;
  art: string;
  innerer_status: string;
  ladedatum: string | null;
  material: string | null;
  notiz: string | null;
  kunde_id: string | null;
  kunde: string | null;
  ladeort_id: string | null;
  ladeort: string | null;
  entladeort_id: string | null;
  entladeort: string | null;
  /**
   * Postleitzahl und Ort der beiden Orte — für die zweite, kleine Zeile unter
   * dem Namen. Die STRASSE steht bewusst nicht in der Sicht: Sie ist der
   * längste Teil einer Anschrift und beantwortet beim Blick auf eine Tour
   * keine Frage.
   */
  ladeort_plz: string | null;
  ladeort_ortsname: string | null;
  entladeort_plz: string | null;
  entladeort_ortsname: string | null;
  fahrer: string;
  fahrzeug: string;
  fahrten: number;
  fahrten_offen: number;
  fuhren: number;
  menge: number | null;
  mengeneinheit: string | null;
  mengen_offen: number;
  beleglage: string | null;
  preisart: string | null;
  preis_vk: number | null;
  /**
   * Die Menge, nach der abgerechnet wird — vom Büro eingetragen (Tonnen laut
   * Wiegeschein). Sie gewinnt gegen die Summe der Fuhrenblöcke: `menge` ist
   * bereits `coalesce(abrechnungsmenge, Summe der Fuhrenblöcke)`.
   */
  abrechnungsmenge: number | null;
  /** Notizen der Fahrer ans Büro, je Zeile „Name: Text". Nie im Leistungsnachweis. */
  fahrer_notizen: string | null;
  rechnungsnummer: string | null;
  /**
   * Das Büro hat entschieden, dass die vorhandenen Belege für diese Tour
   * reichen. Ersetzt NICHT die Beleglage — die sagt weiterhin, was fehlt.
   */
  belege_freigegeben: boolean;
  belege_freigabe_grund: string | null;
  belege_freigegeben_von_name: string | null;
  /** Zur Abrechnung freigegeben — erst dann erscheint die Tour dort. */
  abrechnung_freigegeben: boolean;
  abrechnung_freigegeben_von_name: string | null;
  storno_grund: string | null;
  herkunft: 'buero' | 'fahrer';
  zustand: Zustand;
  erstellt_am: string;
  geaendert_am: string;
};

export async function uebersichtLaden(): Promise<Auftragszeile[]> {
  const { data, error } = await supabase
    .from('auftrag_uebersicht')
    .select('*')
    .order('ladedatum', { ascending: false, nullsFirst: false })
    .limit(1000);
  if (error) throw new Speicherfehler(alsKlarfehler(error));
  return data as Auftragszeile[];
}

/**
 * Was „Aufträge abrechnen" braucht — in der Datenbank ausgewählt, nicht im
 * Browser.
 *
 * ═══ WARUM NICHT EINFACH ALLES LADEN UND FILTERN ═══
 *
 * `uebersichtLaden` hört bei 1000 Zeilen auf und sortiert nach Ladedatum
 * absteigend. Nach einigen Monaten Pendelverkehr fallen deshalb die ÄLTEREN
 * Touren still heraus — und ausgerechnet die noch nicht abgerechneten sind die,
 * die man dort sucht. Sie erst im Browser auszusortieren heißt, dass das Limit
 * die falschen trifft.
 *
 * Deshalb zwei Abfragen mit eigenem Filter: Was offen ist, kommt vollständig
 * (das sind nie viele), und die abgerechneten sind ein Archiv, bei dem das
 * Abschneiden der ältesten harmlos ist.
 */
export async function abrechnungLaden(): Promise<Auftragszeile[]> {
  const [offen, erledigt] = await Promise.all([
    supabase
      .from('auftrag_uebersicht')
      .select('*')
      .in('zustand', ['abrechenbar', 'abrechnungsreif', 'belege_fehlen'])
      .order('ladedatum', { ascending: false, nullsFirst: false })
      .limit(1000),
    supabase
      .from('auftrag_uebersicht')
      .select('*')
      .eq('zustand', 'abgerechnet')
      .order('ladedatum', { ascending: false, nullsFirst: false })
      .limit(1000),
  ]);
  if (offen.error) throw new Speicherfehler(alsKlarfehler(offen.error));
  if (erledigt.error) throw new Speicherfehler(alsKlarfehler(erledigt.error));
  return [...(offen.data as Auftragszeile[]), ...(erledigt.data as Auftragszeile[])];
}

/* --------------------------------------------------------------- Detail */

export type Fahrtzeile = {
  id: string;
  auftrag_id: string;
  rolle: string;
  fahrer_id: string | null;
  fahrzeug_id: string | null;
  datum: string;
  status: string;
  bemerkung: string | null;
  durch_fahrer_angelegt: boolean;
  angelegt_von: string | null;
  erstellt_am: string;
  geaendert_am: string;
};

/** Eine Strecke einer Pendeltour — der Fahrer zählt an ihr die Fuhren. */
export type Streckenzeile = {
  id: string;
  fahrt_id: string;
  ladeort_id: string | null;
  entladeort_id: string | null;
  anzahl: number;
  erstellt_am: string;
};

export type Dokumentzeile = {
  id: string;
  auftrag_id: string | null;
  fahrt_id: string | null;
  art: string;
  storage_pfad: string;
  dateiname: string | null;
  mimetype: string | null;
  groesse_bytes: number | null;
  erfasst_am: string;
  hochgeladen_am: string | null;
  bemerkung: string | null;
};

export type Ereignis = {
  id: string;
  was: string;
  feld: string | null;
  alt: string | null;
  neu: string | null;
  durch: string | null;
  erstellt_am: string;
};

/**
 * Eine Zeile im Verlauf — und woher sie weiß, was sie behauptet.
 *
 * ═══ WARUM ES ZWEI QUELLEN GIBT ═══
 *
 * Der Verlauf war bei allen älteren Touren leer, und das war kein Fehler:
 * Aufgezeichnet wird erst, seit die Auslöser in der Datenbank stehen. Was
 * davor passiert ist, hat niemand mitgeschrieben.
 *
 * Naheliegend wäre gewesen, die fehlenden Zeilen nachträglich in die
 * Protokolltabelle zu schreiben. Das wäre falsch: Ein Protokoll, in das man
 * hinterher Einträge legt, ist als Nachweis nichts mehr wert — weder vor dem
 * Finanzamt noch im Streit mit einem Kunden. Wer einmal rückwirkend schreiben
 * darf, kann alles geschrieben haben.
 *
 * Stattdessen zeigt der Verlauf zusätzlich das, was die Daten SELBST datiert
 * mitbringen: wann die Tour eingetragen wurde, wann welche Fahrt entstand,
 * wann ein Beleg fotografiert wurde. Diese Zeilen sind keine Aufzeichnung,
 * sondern eine Ableitung — und stehen genau so da.
 */
export type Verlaufszeile = {
  id: string;
  wann: string;
  was: string;
  einzelheit: string | null;
  wer: string | null;
  /**
   * `aufgezeichnet` — im Moment des Geschehens protokolliert, unveränderlich.
   * `abgeleitet` — aus einem Datum am Datensatz erschlossen. Sagt zuverlässig,
   * DASS etwas da ist, aber nichts darüber, ob es zwischendurch geändert wurde.
   */
  quelle: 'aufgezeichnet' | 'abgeleitet';
};

export type Auftragsdetail = {
  kopf: Auftragszeile;
  fahrten: Fahrtzeile[];
  /** Die Fuhrenblöcke aller Fahrten — bei Pendeltouren die Relationen. */
  strecken: Streckenzeile[];
  dokumente: Dokumentzeile[];
  verlauf: Verlaufszeile[];
};

export async function auftragLaden(id: string): Promise<Auftragsdetail> {
  const kopf = await supabase
    .from('auftrag_uebersicht')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (kopf.error) throw new Speicherfehler(alsKlarfehler(kopf.error));
  if (!kopf.data) {
    throw new Speicherfehler({
      art: 'regel',
      titel: 'Diesen Auftrag gibt es nicht',
      text: 'Er wurde gelöscht, oder die Adresse stimmt nicht.',
      original: `kein Auftrag mit ${id}`,
    });
  }

  const [fahrten, verlauf, leute] = await Promise.all([
    supabase
      .from('fahrt')
      .select('*')
      .eq('auftrag_id', id)
      .is('geloescht_am', null)
      .order('datum'),
    supabase
      .from('auftrag_ereignis')
      .select('*')
      .eq('auftrag_id', id)
      .order('erstellt_am', { ascending: false })
      .limit(200),
    supabase.from('mitarbeiter').select('id, vorname, nachname'),
  ]);

  // ═══ JEDE ABFRAGE WIRD GEPRÜFT, NICHT NUR DIE ERSTE ═══
  //
  // Vorher sah nur die Kopfabfrage jemand an. Scheiterte `dokument`, stand
  // „Dokumente (0)" da — und weil `loeschbar` an genau dieser Zahl hängt, bot
  // die Seite obendrein „Löschen" an. Die Datenbank hätte es verhindert, aber
  // die Anzeige behauptete das Gegenteil der Lage: eine Abfragepanne als
  // Entwarnung. Das ist derselbe Fehler, der schon einmal „Offene Punkte"
  // getroffen hat.
  for (const antwort of [fahrten, verlauf, leute]) {
    if (antwort.error) throw new Speicherfehler(alsKlarfehler(antwort.error));
  }

  const fahrtKennungen = ((fahrten.data ?? []) as Fahrtzeile[]).map((f) => f.id);

  // Belege hängen entweder direkt am Auftrag oder an einer seiner Fahrten.
  // Beides abfragen — sonst fehlt die Hälfte.
  const [amAuftrag, anFahrt, strecken] = await Promise.all([
    supabase.from('dokument').select('*').eq('auftrag_id', id),
    fahrtKennungen.length
      ? supabase.from('dokument').select('*').in('fahrt_id', fahrtKennungen)
      : Promise.resolve({ data: [], error: null }),
    fahrtKennungen.length
      ? supabase
          .from('fuhrenblock')
          .select('id, fahrt_id, ladeort_id, entladeort_id, anzahl, erstellt_am')
          .in('fahrt_id', fahrtKennungen)
          .order('erstellt_am')
      : Promise.resolve({ data: [], error: null }),
  ]);

  for (const antwort of [amAuftrag, anFahrt, strecken]) {
    if (antwort.error) throw new Speicherfehler(alsKlarfehler(antwort.error));
  }

  const dokumente = new Map<string, Dokumentzeile>();
  for (const d of [
    ...((amAuftrag.data ?? []) as Dokumentzeile[]),
    ...((anFahrt.data ?? []) as Dokumentzeile[]),
  ]) {
    dokumente.set(d.id, d);
  }

  const name = new Map(
    (
      (leute.data ?? []) as { id: string; vorname: string; nachname: string }[]
    ).map((m) => [m.id, `${m.vorname} ${m.nachname}`]),
  );

  const auftrag = kopf.data as Auftragszeile;
  const alleFahrten = (fahrten.data ?? []) as Fahrtzeile[];
  const alleDokumente = [...dokumente.values()].sort((a, b) =>
    a.erfasst_am.localeCompare(b.erfasst_am),
  );

  return {
    kopf: auftrag,
    fahrten: alleFahrten,
    strecken: (strecken.data ?? []) as Streckenzeile[],
    dokumente: alleDokumente,
    verlauf: verlaufBauen(
      auftrag,
      alleFahrten,
      alleDokumente,
      (verlauf.data ?? []) as Ereignis[],
      name,
    ),
  };
}

/**
 * Aufgezeichnetes und Abgeleitetes zu einer Zeitleiste zusammenlegen.
 *
 * Doppelt darf nichts erscheinen: Für eine Tour, die seit der Einführung des
 * Protokolls entstanden ist, gibt es das Ereignis „angelegt" — dann wird es
 * nicht noch einmal aus `erstellt_am` erschlossen. Sonst stünde bei neuen
 * Touren zweimal dasselbe, und der Verlauf verlöre genau die Eigenschaft, für
 * die es ihn gibt: dass man ihm glauben kann.
 */
/** „2026-08-29" liest im Büro niemand. */
function tagesdatum(wert: string): string {
  return new Date(wert + 'T12:00:00').toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function verlaufBauen(
  auftrag: Auftragszeile,
  fahrten: Fahrtzeile[],
  dokumente: Dokumentzeile[],
  ereignisse: Ereignis[],
  name: Map<string, string>,
): Verlaufszeile[] {
  const zeilen: Verlaufszeile[] = ereignisse.map((e) => ({
    id: e.id,
    wann: e.erstellt_am,
    // Bei „geändert" und „zugewiesen" ist das Feld die eigentliche Nachricht:
    // „Preis" sagt etwas, „Geändert" sagt nichts.
    was: e.feld ?? EREIGNISTEXT[e.was] ?? e.was,
    einzelheit:
      e.alt !== null || e.neu !== null
        ? `${e.alt ?? '—'} → ${e.neu ?? '—'}`
        : null,
    wer: e.durch ? (name.get(e.durch) ?? null) : null,
    quelle: 'aufgezeichnet',
  }));

  const schonAufgezeichnet = new Set(ereignisse.map((e) => e.was));

  if (!schonAufgezeichnet.has('angelegt')) {
    zeilen.push({
      id: `abgeleitet:angelegt`,
      wann: auftrag.erstellt_am,
      was: auftrag.herkunft === 'fahrer' ? 'Vom Fahrer angelegt' : 'Vom Büro eingetragen',
      einzelheit: auftrag.nummer,
      wer: null,
      quelle: 'abgeleitet',
    });
  }

  for (const fahrt of fahrten) {
    // Fahrten, die das Protokoll schon kennt, nicht ein zweites Mal.
    if (
      ereignisse.some((e) => e.was === 'fahrt_angelegt' && e.neu === fahrt.datum)
    ) {
      continue;
    }
    zeilen.push({
      id: `abgeleitet:fahrt:${fahrt.id}`,
      wann: fahrt.erstellt_am ?? auftrag.erstellt_am,
      was: fahrt.durch_fahrer_angelegt ? 'Fahrt vom Fahrer angelegt' : 'Fahrt eingeplant',
      einzelheit: tagesdatum(fahrt.datum),
      // Wer sie ANGELEGT hat, nicht wer sie fährt. In einer Spalte „Wer" den
      // zugewiesenen Fahrer zu zeigen, liest sich wie „Martin hat das
      // eingetragen" — und das ist im Zweifel genau die falsche Auskunft.
      wer: fahrt.angelegt_von ? (name.get(fahrt.angelegt_von) ?? null) : null,
      quelle: 'abgeleitet',
    });
  }

  for (const dokument of dokumente) {
    zeilen.push({
      id: `abgeleitet:dokument:${dokument.id}`,
      wann: dokument.erfasst_am,
      was: 'Beleg erfasst',
      einzelheit: dokument.dateiname ?? dokument.art,
      wer: null,
      quelle: 'abgeleitet',
    });
  }

  if (auftrag.rechnungsnummer && !schonAufgezeichnet.has('abgerechnet')) {
    zeilen.push({
      id: 'abgeleitet:rechnung',
      wann: auftrag.geaendert_am,
      was: 'Rechnungsnummer eingetragen',
      einzelheit: auftrag.rechnungsnummer,
      wer: null,
      quelle: 'abgeleitet',
    });
  }

  return zeilen.sort((a, b) => b.wann.localeCompare(a.wann));
}

/**
 * Die Kürzel, die die Auslöser in der Datenbank schreiben, in Klartext.
 *
 * Nur die Ereignisse OHNE Feldangabe stehen hier — bei „geändert" und
 * „zugewiesen" trägt das Feld die Aussage („Preis", „Fahrer") und wird
 * stattdessen angezeigt.
 */
const EREIGNISTEXT: Record<string, string> = {
  angelegt: 'Tour eingetragen',
  geaendert: 'Geändert',
  zugewiesen: 'Zugewiesen',
  fahrt_angelegt: 'Fahrt eingeplant',
  fahrt_status: 'Fahrt',
  abgerechnet: 'Abgerechnet',
  storniert: 'Storniert',
};

export async function notizSetzen(
  auftrag: Auftragszeile,
  notiz: string,
): Promise<void> {
  await aendern({
    tabelle: 'transportauftrag',
    id: auftrag.id,
    gelesenerStand: auftrag.geaendert_am,
    werte: { notiz: notiz.trim() || null },
    was: `Der Auftrag ${auftrag.nummer ?? ''}`.trim(),
    // Die Seite frischt nach einem Konflikt auf und lässt die Eingabe stehen
    // (H1.3b) — „Lade die Seite neu" wäre hier der Rat, sie wegzuwerfen.
    danach: NEUER_STAND_GELADEN,
  });
}

/**
 * Die Rechnungsnummer — die Sperre des ganzen Systems.
 *
 * Bewusst OHNE Statuswechsel. Die Sperre hängt an diesem Feld
 * (`auftrag_noch_aenderbar` prüft `rechnungsnummer is null`), nicht am Status;
 * den Auftrag zusätzlich durch drei Statusstufen zu schieben wäre Zeremonie
 * ohne Wirkung — und jede Stufe hätte eine Bedingung, an der das Eintragen
 * scheitern könnte.
 */
export async function rechnungsnummerSetzen(
  auftrag: Auftragszeile,
  nummer: string,
): Promise<void> {
  await aendern({
    tabelle: 'transportauftrag',
    id: auftrag.id,
    gelesenerStand: auftrag.geaendert_am,
    werte: { rechnungsnummer: nummer.trim() },
    was: `Der Auftrag ${auftrag.nummer ?? ''}`.trim(),
  });
}

export async function preisSetzen(
  auftrag: Auftragszeile,
  werte: {
    preisart?: string | null;
    preis_vk?: number | null;
    /**
     * Nur bei „nach Menge" wirksam — und dort entscheidend: Bei `fuhre`
     * rechnet `betrag()` mit der Fuhrenzahl, sonst mit der Menge. Ohne diese
     * Angabe stünde bei einem Preis je Fuhre der Tonnenbetrag auf der
     * Rechnung.
     */
    mengeneinheit?: string | null;
    /** Die Menge, nach der abgerechnet wird (z. B. Tonnen laut Wiegeschein). */
    abrechnungsmenge?: number | null;
  },
): Promise<void> {
  await aendern({
    tabelle: 'transportauftrag',
    id: auftrag.id,
    gelesenerStand: auftrag.geaendert_am,
    werte,
    was: `Der Auftrag ${auftrag.nummer ?? ''}`.trim(),
  });
}

/* ------------------------------------------------------ Bearbeiten */

/**
 * Ein Auftrag von Hand ändern — alle Felder, nicht nur die bequemen.
 *
 * ═══ WARUM DAS BRAUCHT, WAS ES BRAUCHT ═══
 *
 * Im Alltag verrutscht alles einmal: Das Ladedatum war der Donnerstag und
 * nicht der Mittwoch, der Kunde hat die Baustelle gewechselt, das Material
 * hieß anders. Ohne einen Weg, das geradezuziehen, entsteht die Korrektur als
 * ZWEITER Auftrag — und dann stehen beide in der Auswertung.
 *
 * Gesichert ist es dreifach, und keine der drei Sicherungen ist im Browser:
 * `geaendert_am` verhindert das stille Überschreiben einer fremden Änderung,
 * `auftrag_noch_aenderbar` sperrt ab der Rechnungsnummer, und der Auslöser
 * `protokolliere_auftrag` schreibt jede einzelne Änderung mit.
 */
export async function auftragAendern(
  auftrag: Auftragszeile,
  werte: Record<string, unknown>,
): Promise<void> {
  await aendern({
    tabelle: 'transportauftrag',
    id: auftrag.id,
    gelesenerStand: auftrag.geaendert_am,
    werte,
    was: `Der Auftrag ${auftrag.nummer ?? ''}`.trim(),
    danach: NEUER_STAND_GELADEN,
  });
}

/* -------------------------------------------------- Freigabe und Storno */

/**
 * „Diese Tour darf auf die Rechnung."
 *
 * ═══ WARUM DAS EINE ENTSCHEIDUNG IST UND KEINE FOLGERUNG ═══
 *
 * Ob eine gefahrene Tour diese Woche abgerechnet wird, hängt an Dingen, die
 * das System nicht kennt: eine Rückfrage beim Kunden, ein Preis, der noch
 * verhandelt wird, ein Sammelauftrag, der auf die Schwestertour wartet.
 *
 * Solange die Abrechnungsliste sich selbst füllte, standen dort Touren, die
 * das Büro überspringen musste. Nach der dritten übersprungenen Zeile
 * überspringt man die Liste. Jetzt steht dort nur, was jemand hineingestellt
 * hat — und was dort steht, wird abgerechnet.
 */
/**
 * ═══ WARUM HIER DIE ANTWORT GEPRÜFT WIRD UND NICHT NUR DER FEHLER ═══
 *
 * Die Datenbankfunktionen geben `false` zurück, wenn sie keine Zeile getroffen
 * haben — ohne einen Fehler zu melden. Wer nur `error` prüft, hält ein
 * folgenloses Nichts für Erfolg, frischt die Ansicht auf und zeigt denselben
 * Stand wie vorher. Im Büro sieht das aus, als hätte der Klick nicht gezählt.
 */
async function schalten(
  funktion: string,
  parameter: Record<string, unknown>,
  wasSchiefging: string,
): Promise<void> {
  const { data, error } = await supabase.rpc(funktion, parameter);
  if (error) throw new Speicherfehler(alsKlarfehler(error));

  if (data === false) {
    throw new Speicherfehler({
      art: 'regel',
      titel: wasSchiefging,
      text: 'Die Datenbank hat keine Zeile geändert. Lade die Seite neu — steht danach immer noch der alte Stand da, hat jemand anderes die Tour inzwischen angefasst.',
      original: `${funktion} hat false zurückgegeben`,
    });
  }
}

/**
 * Was einer Tour zur Abrechnung noch fehlt — in Sätzen, nicht in Kürzeln.
 *
 * ═══ WARUM DAS NICHT SPERRT, SONDERN NUR NACHFRAGT ═══
 *
 * Es gibt gute Gründe, eine unvollständige Tour freizugeben: Der Kunde stellt
 * keinen Ausgangsschein aus, der Preis steht in der Rahmenvereinbarung, die
 * zweite Fahrt wird getrennt abgerechnet. Eine Sperre würde in genau diesen
 * Fällen zum Hindernis, das man umgeht — und Wege um eine Sperre herum sind
 * das Gegenteil von Kontrolle.
 *
 * Eine leere Liste heißt: Es fehlt nichts, es wird nicht nachgefragt. Eine
 * Rückfrage, die immer kommt, wird immer weggeklickt.
 */
export function wasFehltVorFreigabe(auftrag: Auftragszeile): string[] {
  const fehlt: string[] = [];

  if (auftrag.fahrten === 0) {
    fehlt.push('Zu dieser Tour ist keine Fahrt eingeplant.');
  } else if (auftrag.fahrten_offen > 0) {
    fehlt.push(
      auftrag.fahrten_offen === 1
        ? 'Eine Fahrt ist noch nicht abgeschlossen.'
        : `${auftrag.fahrten_offen} Fahrten sind noch nicht abgeschlossen.`,
    );
  }

  if (!auftrag.belege_freigegeben && auftrag.beleglage !== 'vollstaendig') {
    const lage = beleglageText(auftrag.beleglage);
    // Bei unbekannter Beleglage liefert `beleglageText` einen Gedankenstrich —
    // „Bei den Belegen —." wäre kein Satz, sondern ein Anzeigefehler.
    fehlt.push(
      lage.art === 'warn'
        ? `Bei den Belegen ${lage.text}.`
        : 'Zur Beleglage liegt nichts vor.',
    );
  }

  // Beim Stundensatz gibt es bewusst keinen Betrag am Auftrag — das ist kein
  // Mangel, sondern die Bauart. Danach zu fragen wäre eine Rückfrage, die man
  // nie ausräumen kann.
  //
  // ═══ SAGEN, WAS WIRKLICH FEHLT ═══
  //
  // Vorher stand hier immer „Es steht noch kein Preis dran" — auch bei einer
  // Tour, an der 13,00 € je Tonne längst standen und nur die Menge fehlte.
  // Aus dem Büro kam: „Ich kann keinen Preis festlegen." Der Preis war da; die
  // Rückfrage behauptete das Gegenteil. Eine falsche Erklärung ist schlimmer
  // als keine.
  if (auftrag.preisart !== 'stundensatz') {
    if (auftrag.preis_vk === null || auftrag.preisart === null) {
      fehlt.push('Es steht noch kein Preis dran.');
    } else if (betrag(auftrag) === null) {
      fehlt.push(
        auftrag.mengeneinheit === 'fuhre'
          ? 'Es ist noch keine Fuhre gezählt.'
          : 'Es fehlt die Menge, nach der abgerechnet wird.',
      );
    }
  }

  return fehlt;
}

/**
 * Was die Fahrer an dieser Tour notiert haben — oder null.
 *
 * Nur gelesen, nie geschrieben, und nie in den Leistungsnachweis: Der geht an
 * den Kunden, und „Standzeit 45 min, Kunde hat gewartet" ist eine Nachricht
 * ans Büro.
 */
export function fahrernotiz(auftrag: Auftragszeile): string | null {
  const text = auftrag.fahrer_notizen?.trim();
  return text ? text : null;
}

/**
 * Alles, was vor einer Freigabe zur Sprache kommt: was fehlt UND was der Fahrer
 * notiert hat.
 *
 * ═══ WARUM EINE NOTIZ ALLEIN SCHON NACHFRAGT ═══
 *
 * Die Freigabe ist die Stelle, an der über den Preis entschieden wird — und eine
 * Standzeit oder ein Umweg gehört genau dort auf den Tisch. Die Rückfrage ist
 * dann keine Sperre und kein Vorwurf, sondern ein Hinweis: Die Knöpfe bleiben
 * „Freigeben" und „Abbrechen".
 *
 * Sie kommt weiterhin nur, wenn es etwas zu sagen gibt. Eine Tour ohne Mangel
 * und ohne Notiz wird ohne Nachfrage freigegeben.
 */
export type Freigabefrage = { fehlt: string[]; notiz: string | null };

export function freigabeFrage(auftrag: Auftragszeile): Freigabefrage | null {
  const fehlt = wasFehltVorFreigabe(auftrag);
  const notiz = fahrernotiz(auftrag);
  return fehlt.length > 0 || notiz !== null ? { fehlt, notiz } : null;
}

export async function abrechnungFreigeben(auftragId: string): Promise<void> {
  await schalten(
    'abrechnung_freigeben',
    { p_auftrag: auftragId },
    'Die Freigabe kam nicht an',
  );
}

export async function abrechnungFreigabeZuruecknehmen(
  auftragId: string,
): Promise<void> {
  await schalten(
    'abrechnung_freigabe_zuruecknehmen',
    { p_auftrag: auftragId },
    'Die Freigabe wurde nicht zurückgenommen',
  );
}

/**
 * Stornieren — mit Begründung, weil eine Tour ohne Umsatz sonst ein Rätsel ist.
 *
 * Rücknehmbar, weil im Alltag die falsche Zeile getroffen wird. Ein Fehler,
 * den man nicht zurücknehmen kann, wird durch einen zweiten Datensatz
 * „repariert" — und dann stehen beide da.
 */
export async function auftragStornieren(
  auftragId: string,
  grund: string,
): Promise<void> {
  // Auch hier die Antwort prüfen und nicht nur den Fehler: `auftrag_stornieren`
  // gibt `false` zurück, wenn die Tour schon eine Rechnungsnummer trägt. Wer
  // das übersieht, zeigt danach eine aufgefrischte Seite ohne Storno und ohne
  // Erklärung — und der Klick sieht aus, als hätte er nicht gezählt.
  await schalten(
    'auftrag_stornieren',
    { p_auftrag: auftragId, p_grund: grund },
    'Das Storno kam nicht an',
  );
}

/**
 * Eine Tour endgültig entfernen — aber nur, wenn es sie nie wirklich gab.
 *
 * ═══ WARUM ES DAS NEBEN DEM STORNO GIBT ═══
 *
 * Stornieren ist richtig für eine Tour, die abgesagt wurde: Sie ist passiert
 * und gehört mit Begründung in die Geschichte. Für eine Tour, die es nie gab —
 * versehentlich zweimal eingetragen — ist es falsch. Die bliebe sonst für immer
 * in jeder Auswertung stehen, und bei jeder Durchsicht müsste jemand von Neuem
 * erkennen, dass sie ein Irrtum war.
 *
 * ═══ DIE BEDINGUNGEN STEHEN IN DER DATENBANK ═══
 *
 * `auftrag_entfernen` lässt es nur zu, solange die Tour nichts hinterlassen
 * hat: keine Belege, keine Fuhren, keine Rechnungsnummer, kein Storno. Diese
 * Schicht bittet nur darum; die Antwort kommt von dort. Eine Prüfung im Browser
 * wäre eine Höflichkeit — und der nächste Bildschirm, der sie vergisst, käme
 * bestimmt.
 *
 * `false` heißt: Die Tour war schon weg. Kein Fehler, sondern der zweite Klick
 * auf denselben Knopf.
 */
export async function auftragEntfernen(auftragId: string): Promise<void> {
  await schalten(
    'auftrag_entfernen',
    { p_auftrag: auftragId },
    'Die Tour wurde nicht gelöscht',
  );
}

export async function auftragStornoZuruecknehmen(auftragId: string): Promise<void> {
  await schalten(
    'auftrag_storno_zuruecknehmen',
    { p_auftrag: auftragId },
    'Das Storno wurde nicht zurückgenommen',
  );
}

/**
 * Eine Rechnungsnummer für einen ganzen Kundenblock.
 *
 * ═══ WARUM NICHT ACHTMAL EINZELN ═══
 *
 * Weil abgerechnet wird, wie abgerechnet wird: EINE Rechnung über acht Touren.
 * Die Nummer achtmal einzutippen ist nicht nur Arbeit, es ist die
 * verlässlichste Art, sich beim sechsten Mal zu vertippen — und danach hängt
 * eine Tour an einer Rechnung, die es nicht gibt.
 *
 * Die Datenbank macht es in EINER Anweisung: Entweder alle oder keiner. Kommt
 * eine Tour nicht durch, weil dort schon eine Nummer steht, wird nichts
 * gespeichert und es steht dort, warum.
 */
export async function rechnungsnummerSetzenViele(
  auftragIds: string[],
  nummer: string,
): Promise<number> {
  // ═══ GETRIMMT UND NACHGEZÄHLT ═══
  //
  // „RE-1042 " mit Leerzeichen — beim Kopieren aus SevDesk kommt es mit — wurde
  // vorher so gespeichert, und danach ließ sich die Nummer nicht mehr
  // korrigieren, denn die Tour ist zu. Und die Trefferzahl wurde
  // weggeworfen: „`false` ist kein Erfolg" gilt in der Mehrzahl genauso. Trifft
  // die Datenbank weniger Touren als verlangt, steht dieselbe Seite wieder da
  // und der Klick sieht aus, als hätte er nicht gezählt.
  const bereinigt = nummer.trim();
  const { data, error } = await supabase.rpc('rechnungsnummer_setzen_viele', {
    p_auftraege: auftragIds,
    p_nummer: bereinigt,
  });
  if (error) throw new Speicherfehler(alsKlarfehler(error));

  const getroffen = Number(data);
  if (getroffen !== auftragIds.length) {
    throw new Speicherfehler({
      art: 'regel',
      titel: 'Nicht alle Touren haben die Rechnungsnummer bekommen',
      text: `Verlangt waren ${auftragIds.length}, geändert wurden ${
        Number.isFinite(getroffen) ? getroffen : 0
      }. Lade die Seite neu und prüfe, welche Touren jetzt „${bereinigt}“ tragen.`,
      original: `rechnungsnummer_setzen_viele gab ${String(data)} zurück, erwartet ${auftragIds.length}`,
    });
  }
  return getroffen;
}

/* ---------------------------------------------------------- Belegfreigabe */

/**
 * „Für diese Tour reichen die Belege."
 *
 * ═══ WARUM ES DAFÜR KEINE REGEL GIBT ═══
 *
 * Gefragt, woran man vorher erkennt, dass eine Tour mit einem einzigen Beleg
 * abgerechnet werden kann, lautet die Antwort: gar nicht. Es hängt weder am
 * Kunden noch an der Strecke noch an der Auftragsart, sondern am Einzelfall.
 *
 * Eine erfundene Regel wäre hier das Schlechteste gewesen: Sie würde in
 * neunzig Prozent der Fälle stimmen, in zehn Prozent still danebenliegen, und
 * niemand würde die zehn Prozent je bemerken. Also entscheidet ein Mensch,
 * einmal je Tour — und die Entscheidung wird mit Namen und Begründung
 * festgehalten.
 *
 * Die Begründung ist Pflicht (die Datenbank besteht darauf). In einem Jahr
 * lautet die Frage bei einer Prüfung nie „wurde freigegeben", sondern „warum".
 */
export async function belegeFreigeben(
  auftragId: string,
  grund: string,
): Promise<void> {
  const { data, error } = await supabase.rpc('belege_freigeben', {
    p_auftrag: auftragId,
    p_grund: grund,
  });
  if (error) throw new Speicherfehler(alsKlarfehler(error));
  if (data === false) {
    throw new Speicherfehler({
      art: 'regel',
      titel: 'Die Belegfreigabe kam nicht an',
      text: 'Die Datenbank hat keine Zeile geändert. Lade die Seite neu.',
      original: 'belege_freigeben hat false zurückgegeben',
    });
  }
}

export async function belegfreigabeZuruecknehmen(auftragId: string): Promise<void> {
  await schalten(
    'belege_freigabe_zuruecknehmen',
    { p_auftrag: auftragId },
    'Die Belegfreigabe wurde nicht zurückgenommen',
  );
}

/* --------------------------------------------------------- Wer fährt */

/**
 * Fahrer und Fahrzeug einer einzelnen Fahrt setzen.
 *
 * ═══ WARUM DAS HIER STEHT UND NICHT AUF DER SEITE ═══
 *
 * Vorher schrieb die Detailsicht direkt in die Tabelle `fahrt` — mit eigener
 * Fehlerbehandlung und eigenem Umgang mit `geaendert_am`. Damit gab es zwei
 * Stellen, an denen der gleichzeitige Zugriff geregelt war, und nur eine davon
 * war die geprüfte. Jede Änderung an Daten gehört in diese Schicht, damit es
 * bei einer Regel bleibt.
 */
export async function fahrtBesetzen(
  fahrt: Fahrtzeile,
  werte: { fahrer_id: string | null; fahrzeug_id: string | null },
): Promise<void> {
  await aendern({
    tabelle: 'fahrt',
    id: fahrt.id,
    gelesenerStand: fahrt.geaendert_am,
    werte,
    was: 'Die Fahrt',
    danach: NEUER_STAND_GELADEN,
  });
}

/* ------------------------------------------------ Fahrt und Strecken nachtragen */

/**
 * Die Fahrt nachtragen, die beim Eintragen der Tour nicht durchkam.
 *
 * Ohne Fahrt taucht eine Tour bei keinem Fahrer auf — und in der Detailsicht
 * gab es keinen Weg, das zu beheben. Der Hinweis „in der Detailsicht
 * nachtragen" wäre sonst ein Versprechen ohne Knopf.
 *
 * Fahrer und Fahrzeug bleiben offen; besetzt wird über „Bearbeiten", damit es
 * dafür EINEN Weg gibt. Die Kennung entsteht im Browser, wie überall.
 */
export async function fahrtNachtragen(auftrag: Auftragszeile): Promise<string> {
  const kennung = crypto.randomUUID();
  const { error } = await supabase.from('fahrt').insert({
    id: kennung,
    auftrag_id: auftrag.id,
    rolle: 'komplett',
    datum: auftrag.ladedatum,
  });
  if (error) throw new Speicherfehler(alsKlarfehler(error));
  return kennung;
}

/**
 * Eine Relation an eine Pendeltour hängen — Fuhrenblock mit Anzahl 0.
 *
 * Nur solange noch keine Fuhre gezählt ist: Danach hat der Fahrer schon gegen
 * die vorhandenen Strecken gearbeitet, und eine nachgetragene würde die
 * Zählung nicht mehr erklären. Die Sperre steht in der Oberfläche; die
 * Datenbank lässt es ohnehin nur zu, solange `auftrag_noch_aenderbar` gilt.
 */
export async function streckeNachtragen(
  fahrtId: string,
  ladeortId: string,
  entladeortId: string,
): Promise<void> {
  const { error } = await supabase.from('fuhrenblock').insert({
    id: crypto.randomUUID(),
    fahrt_id: fahrtId,
    ladeort_id: ladeortId,
    entladeort_id: entladeortId,
    anzahl: 0,
    mengeneinheit: 'fuhre',
  });
  if (error) throw new Speicherfehler(alsKlarfehler(error));
}

/* ------------------------------------------------------------- Dokumente */

/**
 * Eine Datei vom Büro an den Auftrag hängen.
 *
 * Der Auftrag des Kunden kommt als PDF oder Foto herein, lange bevor jemand
 * losfährt — und gehört an dieselbe Stelle wie die Lieferscheine, die der
 * Fahrer später fotografiert. Sonst liegt die eine Hälfte im Backoffice und
 * die andere im Mailpostfach.
 *
 * Der Pfad trägt Jahr und Monat, damit der Speicher nach zwei Jahren nicht
 * ein einziger Ordner mit zehntausend Dateien ist.
 */
export async function dokumentHochladen(
  auftragId: string,
  datei: File,
  art: string,
): Promise<void> {
  const jetzt = new Date();
  const kennung = crypto.randomUUID();
  const endung = datei.name.includes('.')
    ? datei.name.slice(datei.name.lastIndexOf('.'))
    : '';
  const pfad = `buero/${jetzt.getFullYear()}/${String(jetzt.getMonth() + 1).padStart(2, '0')}/${kennung}${endung}`;

  const { error: speicherFehler } = await supabase.storage
    .from('belege')
    .upload(pfad, datei, { contentType: datei.type || undefined });

  if (speicherFehler) {
    throw new Speicherfehler({
      art: 'unbekannt',
      titel: 'Die Datei konnte nicht abgelegt werden',
      text: speicherFehler.message,
      original: speicherFehler.message,
    });
  }

  try {
    // ═══ `erfasst_am` IST PFLICHT — UND FEHLTE HIER ═══
    //
    // Die Spalte hat keinen Vorgabewert. Jeder Upload aus dem Büro scheiterte
    // deshalb mit „null value in column erfasst_am violates not-null
    // constraint" — von Anfang an, bei jeder Datei, unabhängig von Größe und
    // Typ. Gesehen hat es nur niemand: Die Meldung landete am Seitenanfang,
    // weit über der Dokumentenkarte, und im Büro las sich das als „mein PDF
    // wird blockiert".
    //
    // Warum derselbe Zeitpunkt wie `hochgeladen_am`: `erfasst_am` ist der
    // Zeitpunkt auf dem GERÄT und weicht bei der Fahrer-App ab, weil dort im
    // Funkloch erfasst und später zugestellt wird. Genau diese Differenz ist
    // bei Rückfragen die interessante Größe. Im Büro gibt es sie nicht — hier
    // ist Auswählen und Ablegen derselbe Augenblick. Zwei gleiche Zeitpunkte
    // sind die ehrliche Aussage: keine Verzögerung.
    const jetztAlsText = new Date().toISOString();
    await anlegen('dokument', {
      id: kennung,
      auftrag_id: auftragId,
      art,
      storage_pfad: pfad,
      dateiname: datei.name,
      mimetype: datei.type || null,
      groesse_bytes: datei.size,
      quelle: 'buero',
      erfasst_am: jetztAlsText,
      hochgeladen_am: jetztAlsText,
    });
  } catch (problem) {
    // Sonst bliebe eine Datei im Speicher liegen, zu der es keinen Eintrag
    // gibt — und die niemand je wieder findet.
    await supabase.storage.from('belege').remove([pfad]);
    throw problem;
  }
}

/**
 * Einen Dokumenteintrag verwerfen.
 *
 * ═══ WARUM DAS ÜBERHAUPT GEHEN MUSS ═══
 *
 * Es gibt Einträge ohne Datei — aus Testdaten, aus einem abgebrochenen Upload.
 * Sie sind nicht bloß hässlich: Die Beleglage zählt sie mit, und eine Tour
 * gilt dadurch als vollständig belegt, obwohl das Foto fehlt. Ohne einen Weg,
 * sie loszuwerden, müsste man dem Zustand „Belege verfügbar" misstrauen — und
 * dann trägt er nichts mehr bei.
 *
 * Entschieden wird das NICHT hier, sondern von `dokument_verwerfen` in der
 * Datenbank: Hängt der Beleg an einer Tour mit Rechnungsnummer, gibt die
 * Funktion `false` zurück und nichts passiert. Die Sperre gilt für alle Wege
 * in die Daten, nicht nur für den Knopf in dieser Oberfläche.
 */
export async function dokumentVerwerfen(dokument: Dokumentzeile): Promise<void> {
  const { data, error } = await supabase.rpc('dokument_verwerfen', {
    p_dokument: dokument.id,
  });

  if (error) throw new Speicherfehler(alsKlarfehler(error));

  if (data === false) {
    throw new Speicherfehler({
      art: 'regel',
      titel: 'Der Beleg wurde nicht entfernt',
      text: 'Der Eintrag war schon weg, oder deinem Konto fehlt das Recht dazu. Lade die Seite neu — steht er dann noch da, ist es das Recht.',
      original: 'dokument_verwerfen hat false zurückgegeben',
    });
  }

  // Erst die Zeile, dann die Datei — in dieser Reihenfolge. Scheitert das
  // Aufräumen im Speicher, liegt dort eine Datei ohne Eintrag: unschön, aber
  // folgenlos. Andersherum stünde ein Eintrag ohne Datei da, und genau das
  // ist der Fehler, den dieser Knopf beseitigen soll.
  await supabase.storage
    .from('belege')
    .remove([dokument.storage_pfad.replace(/^belege\//, '')]);
}
